/* ARVADA catalog — “products per row” switch.
   Sets --av-cols-d (sidebar layout) / --av-cols-m (drawer layout) on the section, remembers the choice
   and hides modes whose cards would be narrower than data-min px at the current width. */
if (!customElements.get('arvada-grid-density')) {
  customElements.define('arvada-grid-density', class extends HTMLElement {
    connectedCallback() {
      this.key = this.dataset.key === 'm' ? 'm' : 'd';
      this.root = this.closest('collection-component') || document.documentElement;
      this.bp = parseInt(this.dataset.bp, 10) || 950;
      this.min = parseInt(this.dataset.min, 10) || (this.key === 'd' ? 190 : 150);
      this.buttons = [...this.querySelectorAll('[data-cols]')];
      this.values = this.buttons.map((b) => Number(b.dataset.cols));
      let saved = null;
      try { saved = localStorage.getItem(`avGridCols-${this.key}`); } catch (e) {}
      const fallback = this.key === 'm' && this.dataset.defaultTablet && matchMedia('(min-width: 750px)').matches ? this.dataset.defaultTablet : this.dataset.default;
      this.choice = this.values.includes(Number(saved)) ? Number(saved) : this.nearest(Number(fallback));
      this.abort = new AbortController();
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-cols]');
        if (!button || button.hidden) return;
        this.choice = Number(button.dataset.cols);
        try { localStorage.setItem(`avGridCols-${this.key}`, String(this.choice)); } catch (e) {}
        this.update();
      }, { signal: this.abort.signal });
      const schedule = () => { if (this.frame == null) this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); }); };
      addEventListener('resize', schedule, { passive: true, signal: this.abort.signal });
      const container = document.getElementById('ProductGridContainer');
      if (container && 'ResizeObserver' in window) { this.resize = new ResizeObserver(schedule); this.resize.observe(container); }
      this.update();
    }
    nearest(value) {
      if (this.values.includes(value)) return value;
      const lower = this.values.filter((n) => n <= value);
      return lower.length ? Math.max(...lower) : Math.min(...this.values);
    }
    isActive() {
      const wide = matchMedia(`(min-width: ${this.bp + 1}px)`).matches;
      return this.key === 'd' ? wide : !wide;
    }
    update() {
      let fits = this.values;
      const grid = document.getElementById('product-grid');
      if (this.isActive() && grid && grid.clientWidth) {
        const width = grid.clientWidth;
        const gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
        const smallest = Math.min(...this.values);
        fits = this.values.filter((n) => n === smallest || (width - (n - 1) * gap) / n >= this.min);
      }
      const effective = fits.includes(this.choice) ? this.choice : Math.max(...fits.filter((n) => n <= this.choice).concat(Math.min(...fits)));
      this.root.style.setProperty(`--av-cols-${this.key}`, String(effective));
      this.buttons.forEach((b) => {
        const n = Number(b.dataset.cols);
        b.hidden = !fits.includes(n);
        b.setAttribute('aria-pressed', String(n === effective));
      });
      this.classList.toggle('av-density--single', fits.length < 2);
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); }
  });
}

/* Focus rings: keyboard users keep them, a mouse / touchpad / touch interaction does not leave one behind (Sort by). */
(() => {
  const root = document.documentElement;
  const set = (value) => { if (root.dataset.avInput !== value) root.dataset.avInput = value; };
  document.addEventListener('pointerdown', () => set('pointer'), { capture: true, passive: true });
  document.addEventListener('keydown', (event) => {
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    set('keyboard');
  }, true);
  /* A native select keeps focus after a choice made with the mouse — release it once the choice is applied. */
  document.addEventListener('change', (event) => {
    const select = event.target;
    if (root.dataset.avInput === 'pointer' && select instanceof HTMLSelectElement && select.matches('.av-catalog--v1 .facet-filters__sort')) {
      select.classList.remove('focused');
    }
  }, true);
})();

/* Button filters with one choice (e.g. Has Stones: Yes / No). Capture phase: runs before Dawn's debounced submit reads the form. */
document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox' || !input.checked) return;
  const list = input.closest('[data-av-single]');
  if (!list) return;
  list.querySelectorAll('input[type=checkbox]:checked').forEach((other) => {
    if (other === input) return;
    other.checked = false;
    other.closest('label')?.classList.remove('active');
  });
}, true);

/* Sticky filter column (whole column moves as one piece, no inner scrollbar, nothing overlaps it).
   Fits the screen → stays at the top. Taller → follows the page and holds at its bottom edge while scrolling down,
   and at its top edge (selected filters, Remove all) as soon as you scroll back up. */
(() => {
  let aside = null;
  let offset = null;
  let lastY = scrollY;
  let frame = null;
  const update = () => {
    frame = null;
    if (!aside || !aside.isConnected) {
      aside = document.querySelector('.av-catalog--v1.av-sticky-filters .facets-vertical > .facets-wrapper:not(.facets-wrapper--no-filters)');
      offset = null;
    }
    const y = scrollY;
    const dy = y - lastY;
    lastY = y;
    if (!aside) return;
    if (getComputedStyle(aside).position !== 'sticky') { aside.style.removeProperty('--av-side-top'); offset = null; return; }
    const root = aside.closest('collection-component') || document.documentElement;
    const top = parseFloat(getComputedStyle(root).getPropertyValue('--av-filters-top')) || 16;
    const lowest = Math.min(top, innerHeight - aside.offsetHeight);
    if (offset == null) offset = top;
    offset = Math.max(lowest, Math.min(top, offset - dy));
    aside.style.setProperty('--av-side-top', `${Math.round(offset)}px`);
  };
  const schedule = () => { if (frame == null) frame = requestAnimationFrame(update); };
  const start = () => {
    const target = document.querySelector('.av-catalog--v1 .facets-vertical');
    if (target) {
      if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(target);
      new MutationObserver(schedule).observe(target, { subtree: true, childList: true, attributes: true, attributeFilter: ['open'] });
    }
    schedule();
  };
  addEventListener('scroll', schedule, { passive: true });
  addEventListener('resize', schedule, { passive: true });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
